import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { useLenis } from 'lenis/react';

export default function ScrollBar() {
	const scrollbarRef = useRef(null);
	const thumbRef = useRef(null);
		const debugRef = useRef(null);

	// useLenis fournit l'instance Lenis gérée par ReactLenis (dans App.jsx)
	const lenis = useLenis();

	useEffect(() => {
		if (!lenis) return;

		// elements
		const scrollbar = scrollbarRef.current;
		const thumb = thumbRef.current;
		const rail = scrollbar ? scrollbar.querySelector('.scrollbar-inner-rail') : null;
		const scrollerEl = document.querySelector('.container') || document.scrollingElement || document.body;
		if (!scrollbar || !thumb || !scrollerEl || !rail) return;

		let scrollbarHeight;
		let thumbHeight;
		let totalScrollable;
		let currentY = 0;
		let maxY = 0;

		const DEBUG = typeof window !== 'undefined' && window.__SCROLLBAR_DEBUG === true;

		const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

		const setThumbY = (y) => {
			const yClamped = clamp(y, 0, maxY);
			currentY = yClamped;
			try { gsap.set(thumb, { y: yClamped }); } catch (e) { /* ignore */ }
		};

		const updateThumbHeight = () => {
			const contentHeight = scrollerEl.scrollHeight || document.body.scrollHeight || 1;
			const visibleHeight = scrollerEl.clientHeight || window.innerHeight || 1;
			totalScrollable = Math.max(0, contentHeight - visibleHeight);

			scrollbarHeight = rail.getBoundingClientRect().height || 1;
			if (totalScrollable <= 0) {
				thumbHeight = scrollbarHeight;
			} else {
				thumbHeight = Math.max(24, (visibleHeight / contentHeight) * scrollbarHeight);
			}
			maxY = Math.max(0, scrollbarHeight - thumbHeight);
			gsap.set(thumb, { height: thumbHeight });
			// ensure thumb inside bounds
			setThumbY(currentY);
			if (DEBUG) console.log('[ScrollBar] updateThumbHeight', { contentHeight, visibleHeight, scrollbarHeight, thumbHeight, totalScrollable, maxY });
		};

		const onLenisScroll = (e) => {
			try {
				const scroller = scrollerEl;
				let scrollTop;
				let maxScroll;
				let progress;
				if (e && typeof e === 'object') {
					scrollTop = e.scroll ?? (typeof lenis.scroll === 'number' ? lenis.scroll : (scroller.scrollTop || 0));
					maxScroll = e.limit ?? Math.max(1, (scroller.scrollHeight || document.body.scrollHeight) - (scroller.clientHeight || window.innerHeight));
					progress = e.progress ?? (maxScroll ? Math.min(1, Math.max(0, scrollTop / maxScroll)) : 0);
				} else {
					scrollTop = (typeof lenis.scroll === 'number') ? lenis.scroll : (scroller.scrollTop || 0);
					maxScroll = Math.max(1, (scroller.scrollHeight || document.body.scrollHeight) - (scroller.clientHeight || window.innerHeight));
					progress = Math.min(1, Math.max(0, scrollTop / maxScroll));
				}
				if (typeof scrollbarHeight === 'undefined' || typeof thumbHeight === 'undefined') updateThumbHeight();
				const y = progress * maxY;
				setThumbY(y);
				if (DEBUG) console.log('[ScrollBar] onLenisScroll', { scrollTop, maxScroll, progress, y });
				if (DEBUG && debugRef.current) debugRef.current.textContent = `p=${Math.round(progress*100)}% y=${Math.round(y)} s=${Math.round(scrollTop)}`;
			} catch (err) { if (DEBUG) console.error(err); }
		};

		// Pointer drag implementation (supports pointer, mouse and touch)
		let isPointerDown = false;
		let startPointerY = 0;
		let startThumbY = 0;
		// history for velocity calculation (in px/ms)
		let moveHistory = [];

		const startDrag = (clientY, pointerId, target) => {
			isPointerDown = true;
			startPointerY = clientY;
			startThumbY = currentY;
			moveHistory = [{ t: performance.now(), y: clientY }];
			if (DEBUG) console.log('[ScrollBar] startDrag', { clientY, startThumbY, pointerId });
			// UX: disable selection and change cursor
			document.body.style.userSelect = 'none';
			document.documentElement.classList.add('scrollbar-dragging');
			// attach pointer listeners (for pointer events)
			document.addEventListener('pointermove', onPointerMove, { passive: false });
			document.addEventListener('pointerup', onPointerUp);
			document.addEventListener('pointercancel', onPointerUp);
			// attach mouse listeners as fallback
			document.addEventListener('mousemove', onPointerMove, { passive: false });
			document.addEventListener('mouseup', onPointerUp);
			// attach touch listeners as fallback
			document.addEventListener('touchmove', onTouchMove, { passive: false });
			document.addEventListener('touchend', onPointerUp);
			document.addEventListener('touchcancel', onPointerUp);
			try { if (target && target.setPointerCapture) target.setPointerCapture(pointerId); } catch (e) { /* ignore */ }
			scrollbar.setAttribute('data-scrollbar-drag', 'true');
		};

		const stopDrag = () => {
			if (!isPointerDown) return;
			isPointerDown = false;
			if (DEBUG) console.log('[ScrollBar] stopDrag');
			// restore UX
			document.body.style.userSelect = '';
			document.body.style.cursor = '';
			document.documentElement.classList.remove('scrollbar-dragging');
			document.removeEventListener('pointermove', onPointerMove);
			document.removeEventListener('pointerup', onPointerUp);
			document.removeEventListener('pointercancel', onPointerUp);
			document.removeEventListener('mousemove', onPointerMove);
			document.removeEventListener('mouseup', onPointerUp);
			document.removeEventListener('touchmove', onTouchMove);
			document.removeEventListener('touchend', onPointerUp);
			document.removeEventListener('touchcancel', onPointerUp);
			scrollbar.setAttribute('data-scrollbar-drag', 'false');
		};

		const onPointerMove = (ev) => {
			if (!isPointerDown) return;
			if (ev.touches && ev.touches.length) return; // touch handled in onTouchMove
			ev.preventDefault();
			const clientY = ev.clientY;
			// record movement for velocity
			try { moveHistory.push({ t: performance.now(), y: clientY }); if (moveHistory.length > 8) moveHistory.shift(); } catch (e) { /* ignore */ }
			const delta = clientY - startPointerY;
			const newY = clamp(startThumbY + delta, 0, maxY);
			setThumbY(newY);
			const progress = maxY > 0 ? newY / maxY : 0;
			try { lenis.scrollTo(progress * totalScrollable, { immediate: true }); } catch (e) { /* ignore */ }
		};

		const onTouchMove = (ev) => {
			if (!isPointerDown) return;
			ev.preventDefault();
			const touch = ev.touches[0];
			if (!touch) return;
			const clientY = touch.clientY;
			// record movement for velocity
			try { moveHistory.push({ t: performance.now(), y: clientY }); if (moveHistory.length > 8) moveHistory.shift(); } catch (e) { /* ignore */ }
			const delta = clientY - startPointerY;
			const newY = clamp(startThumbY + delta, 0, maxY);
			setThumbY(newY);
			const progress = maxY > 0 ? newY / maxY : 0;
			try { lenis.scrollTo(progress * totalScrollable, { immediate: true }); } catch (e) { /* ignore */ }
		};

		const onPointerUp = (ev) => {
			// compute inertia then stop
			applyMomentum();
			stopDrag();
			try { if (ev && ev.target && ev.target.releasePointerCapture) ev.target.releasePointerCapture(ev.pointerId); } catch (e) { /* ignore */ }
		};

		// compute and apply momentum on release
		const applyMomentum = () => {
			try {
				if (!moveHistory || moveHistory.length < 2) return;
				const last = moveHistory[moveHistory.length - 1];
				// take a point a few items back for a stable velocity
				const first = moveHistory[Math.max(0, moveHistory.length - 6)];
				const dy = last.y - first.y;
				const dt = Math.max(1, last.t - first.t); // ms
				const vy = dy / dt; // px per ms
				// threshold to avoid tiny flicks
				if (Math.abs(vy) < 0.01) return; // <10 px/sec
				// map thumb velocity to scroll velocity
				const factor = (maxY > 0) ? (totalScrollable / maxY) : 0;
				const vScroll = vy * factor; // px per ms
				// momentum duration heuristic (ms)
				const momentumMs = Math.min(1400, Math.max(150, Math.abs(vScroll) * 600));
				// current scroll
				const currentScroll = (typeof lenis.scroll === 'number') ? lenis.scroll : (scrollerEl.scrollTop || 0);
				let target = currentScroll + vScroll * momentumMs;
				// clamp
				target = Math.max(0, Math.min(totalScrollable, target));
				const duration = Math.min(1.6, Math.max(0.2, momentumMs / 1000));
				if (DEBUG) console.log('[ScrollBar] applyMomentum', { vy, vScroll, momentumMs, currentScroll, target, duration });
				try { lenis.scrollTo(target, { duration, immediate: false, easing: (t) => 1 - Math.pow(1 - t, 3) }); } catch (e) { /* ignore */ }
				// clear move history to avoid reusing old velocity
				moveHistory = [];
			} catch (e) { if (DEBUG) console.error(e); }
		}

		// debounce helper
		let mutateTimeout = null;
		const debounceUpdate = () => {
			if (mutateTimeout) clearTimeout(mutateTimeout);
			mutateTimeout = setTimeout(() => { updateThumbHeight(); mutateTimeout = null; }, 120);
		};

		// MutationObserver to adapt to dynamic content
		let mo = null;
		try {
			mo = new MutationObserver(() => debounceUpdate());
			mo.observe(scrollerEl, { childList: true, subtree: true, attributes: true });
		} catch (e) { /* ignore */ }

		const onResize = () => {
			updateThumbHeight();
			onLenisScroll();
		};

		let pollIntervalId = null;
		let gotFirstLenisEvent = false;

		const lenisHandler = (e) => { gotFirstLenisEvent = true; onLenisScroll(e); };

		// init
		updateThumbHeight();
		onLenisScroll({ scroll: (typeof lenis.scroll === 'number') ? lenis.scroll : 0, limit: Math.max(1, (scrollerEl.scrollHeight || 1) - scrollerEl.clientHeight || window.innerHeight) });
		setTimeout(() => { updateThumbHeight(); onLenisScroll(); }, 100);
		setTimeout(() => { updateThumbHeight(); onLenisScroll(); }, 500);
		window.addEventListener('resize', onResize);
		try { lenis.on('scroll', lenisHandler); } catch (e) { /* ignore */ }
		pollIntervalId = setInterval(() => {
			if (gotFirstLenisEvent) {
				clearInterval(pollIntervalId);
				pollIntervalId = null;
				return;
			}
			try { onLenisScroll(); } catch (e) { /* ignore */ }
		}, 100);

		// attach pointer/mouse/touch handlers
		const onPointerDown = (ev) => {
			ev.preventDefault();
			// support pointer event
			if (ev.type === 'pointerdown') {
				startDrag(ev.clientY, ev.pointerId, ev.target);
				return;
			}
			// support mouse
			if (ev.type === 'mousedown') {
				startDrag(ev.clientY, undefined, ev.target);
				return;
			}
			// support touch
			if (ev.type === 'touchstart') {
				const t = ev.touches[0];
				if (!t) return;
				startDrag(t.clientY, undefined, ev.target);
				return;
			}
		};

		const onRailPointerDown = (ev) => {
			// ignore if clicking the thumb directly
			if (ev.target === thumb) return;
			ev.preventDefault();
			const rect = rail.getBoundingClientRect();
			const clientY = (ev.touches && ev.touches[0]) ? ev.touches[0].clientY : ev.clientY;
			const clickY = clientY - rect.top;
			const targetY = clamp(clickY - thumbHeight / 2, 0, maxY);
			// set immediate position for visual feedback
			setThumbY(targetY);
			const progress = maxY > 0 ? targetY / maxY : 0;
			try { lenis.scrollTo(progress * totalScrollable, { immediate: false, duration: 0.6 }); } catch (e) { /* ignore */ }
		};

		thumb.addEventListener('pointerdown', onPointerDown);
		thumb.addEventListener('mousedown', onPointerDown);
		thumb.addEventListener('touchstart', onPointerDown, { passive: false });
		rail.addEventListener('pointerdown', onRailPointerDown);
		rail.addEventListener('mousedown', onRailPointerDown);
		rail.addEventListener('touchstart', onRailPointerDown, { passive: false });

		return () => {
			window.removeEventListener('resize', onResize);
			try { lenis.off && lenis.off('scroll', lenisHandler); } catch (e) { /* ignore */ }
			if (pollIntervalId) { clearInterval(pollIntervalId); pollIntervalId = null; }
			if (mo) try { mo.disconnect(); } catch (e) { /* ignore */ }
			if (mutateTimeout) clearTimeout(mutateTimeout);
			// remove pointer handlers
			try { thumb.removeEventListener('pointerdown', onPointerDown); } catch (e) { /* ignore */ }
			try { thumb.removeEventListener('mousedown', onPointerDown); } catch (e) { /* ignore */ }
			try { thumb.removeEventListener('touchstart', onPointerDown); } catch (e) { /* ignore */ }
			try { rail.removeEventListener('pointerdown', onRailPointerDown); } catch (e) { /* ignore */ }
			try { rail.removeEventListener('mousedown', onRailPointerDown); } catch (e) { /* ignore */ }
			try { rail.removeEventListener('touchstart', onRailPointerDown); } catch (e) { /* ignore */ }
			// ensure pointermove/up removed
			document.removeEventListener('pointermove', onPointerMove);
			document.removeEventListener('pointerup', onPointerUp);
			document.removeEventListener('pointercancel', onPointerUp);
			document.removeEventListener('mousemove', onPointerMove);
			document.removeEventListener('mouseup', onPointerUp);
			document.removeEventListener('touchmove', onTouchMove);
			document.removeEventListener('touchend', onPointerUp);
			document.removeEventListener('touchcancel', onPointerUp);
		};

	}, [lenis]);

	return (
		<div ref={scrollbarRef} className="scrollbar-inner" data-scrollbar="" data-scrollbar-drag="false">
			<div className="scrollbar-inner-rail">
				<div ref={thumbRef} className="scrollbar" data-scrollbar-thumb=""></div>
			</div>
			{typeof window !== 'undefined' && window.__SCROLLBAR_DEBUG === true ? <div ref={debugRef} className="scrollbar-debug" aria-hidden>debug</div> : null}
		</div>
	);
}

